import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { 
  Download, 
  Calendar, 
  ArrowRight, 
  Loader2,
  Target,
  Lightbulb,
  User,
  DollarSign,
  Megaphone,
  ClipboardCheck,
  CalendarDays,
  Share2
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTION_CONFIG = {
  problem: { icon: Target, title: "The Problem", color: "text-red-600" },
  solution: { icon: Lightbulb, title: "The Solution", color: "text-amber-600" },
  founder_fit: { icon: User, title: "Founder Fit", color: "text-blue-600" },
  offer: { icon: DollarSign, title: "Offer & Pricing", color: "text-green-600" },
  go_to_market: { icon: Megaphone, title: "Go-To-Market", color: "text-purple-600" },
  assumptions: { icon: ClipboardCheck, title: "Assumptions & Tests", color: "text-indigo-600" },
  action_plan: { icon: CalendarDays, title: "30-Day Action Plan", color: "text-pink-600" }
};

const FIELD_LABELS = {
  target_customer: "Target Customer",
  specific_problem: "Specific Problem",
  moment_of_pain: "Moment of Pain",
  cost_of_problem: "Cost of the Problem",
  one_sentence_solution: "One-Sentence Solution",
  top_3_outcomes: "Top 3 Outcomes",
  deliberately_excluded: "What's Deliberately Excluded",
  founder_advantage: "Founder Advantage",
  why_now: "Why Now?",
  first_offer: "First Offer",
  simple_pricing: "Simple Pricing",
  what_customer_gets: "What the Customer Gets",
  primary_acquisition_channel: "Primary Acquisition Channel",
  first_10_customers_plan: "First 10 Customers Plan",
  outreach_hook: "Outreach Hook",
  top_3_assumptions: "Top 3 Assumptions",
  validation_tests: "Validation Tests",
  success_criteria: "Success Criteria",
  week_1: "Week 1",
  week_2: "Week 2",
  week_3: "Week 3",
  week_4: "Week 4"
};

export default function CanvasViewPage() {
  const { canvasId } = useParams();
  const [canvas, setCanvas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const printRef = useRef(null);

  useEffect(() => {
    fetchCanvas();
  }, [canvasId]);

  const fetchCanvas = async () => {
    try {
      const response = await axios.get(`${API}/canvas/${canvasId}`);
      setCanvas(response.data);
    } catch (error) {
      toast.error("Failed to load canvas");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      // Create a printable version
      const printContent = printRef.current;
      const printWindow = window.open('', '_blank');
      
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>${canvas?.name || 'Validation Canvas'} - FoundrScore</title>
          <style>
            body { 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
              padding: 40px;
              max-width: 900px;
              margin: 0 auto;
              color: #111827;
            }
            h1 { font-size: 24px; margin-bottom: 8px; }
            h2 { font-size: 18px; color: #2563EB; margin-top: 24px; margin-bottom: 12px; border-bottom: 2px solid #E5E7EB; padding-bottom: 8px; }
            h3 { font-size: 14px; color: #6B7280; margin-bottom: 4px; }
            p { margin: 0 0 16px 0; line-height: 1.6; }
            .header { text-align: center; margin-bottom: 40px; }
            .section { margin-bottom: 24px; page-break-inside: avoid; }
            .field { margin-bottom: 16px; }
            ul { margin: 0; padding-left: 20px; }
            li { margin-bottom: 8px; }
            .footer { text-align: center; margin-top: 40px; padding-top: 20px; border-top: 1px solid #E5E7EB; color: #9CA3AF; font-size: 12px; }
            @media print {
              body { padding: 20px; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${canvas?.name || 'Validation Canvas'}</h1>
            <p style="color: #6B7280;">Created with FoundrScore</p>
          </div>
          ${printContent.innerHTML}
          <div class="footer">
            <p>Generated by FoundrScore - foundrscore.com</p>
          </div>
        </body>
        </html>
      `);
      
      printWindow.document.close();
      printWindow.focus();
      
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
      
      toast.success("PDF ready for download!");
    } catch (error) {
      toast.error("Failed to generate PDF");
    } finally {
      setDownloading(false);
    }
  };

  const trackBookingClick = async () => {
    try {
      await axios.post(`${API}/analytics/track`, {
        event_type: "booking_click",
        event_data: { canvas_id: canvasId }
      });
    } catch (error) {
      console.error("Failed to track:", error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!canvas) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center">
        <p className="text-muted-foreground mb-4">Canvas not found</p>
        <Link to="/canvas">
          <Button>Create New Canvas</Button>
        </Link>
      </div>
    );
  }

  const renderValue = (value) => {
    if (Array.isArray(value)) {
      return (
        <ul className="list-disc list-inside space-y-1">
          {value.filter(v => v).map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
      );
    }
    return <p className="text-foreground">{value || "Not filled"}</p>;
  };

  const sections = canvas.sections || {};

  return (
    <div className="py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">{canvas.name}</h1>
          <p className="text-muted-foreground">
            Your One-Page Validation Canvas
          </p>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap justify-center gap-4 mb-8 no-print">
          <Button 
            onClick={handleDownloadPDF} 
            disabled={downloading}
            className="rounded-full"
            data-testid="download-pdf-btn"
          >
            {downloading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Download PDF
          </Button>
          <a href="https://calendly.com" target="_blank" rel="noopener noreferrer" onClick={trackBookingClick}>
            <Button variant="outline" className="rounded-full" data-testid="book-call-btn">
              <Calendar className="w-4 h-4 mr-2" />
              Book Strategy Call
            </Button>
          </a>
        </div>

        {/* Canvas Content - Printable */}
        <div ref={printRef} data-testid="canvas-content">
          {/* Problem Section */}
          <Card className="mb-6">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Target className="w-5 h-5 text-red-600" />
                The Problem
              </CardTitle>
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-4">
              <div className="field">
                <h3 className="text-sm font-medium text-muted-foreground mb-1">Target Customer</h3>
                {renderValue(sections.target_customer)}
              </div>
              <div className="field">
                <h3 className="text-sm font-medium text-muted-foreground mb-1">Specific Problem</h3>
                {renderValue(sections.specific_problem)}
              </div>
              <div className="field">
                <h3 className="text-sm font-medium text-muted-foreground mb-1">Moment of Pain</h3>
                {renderValue(sections.moment_of_pain)}
              </div>
              <div className="field">
                <h3 className="text-sm font-medium text-muted-foreground mb-1">Cost of the Problem</h3>
                {renderValue(sections.cost_of_problem)}
              </div>
            </CardContent>
          </Card>

          {/* Solution Section */}
          <Card className="mb-6">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Lightbulb className="w-5 h-5 text-amber-600" />
                The Solution
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="field">
                <h3 className="text-sm font-medium text-muted-foreground mb-1">One-Sentence Solution</h3>
                {renderValue(sections.one_sentence_solution)}
              </div>
              <div className="field">
                <h3 className="text-sm font-medium text-muted-foreground mb-1">Top 3 Outcomes</h3>
                {renderValue(sections.top_3_outcomes)}
              </div>
              <div className="field">
                <h3 className="text-sm font-medium text-muted-foreground mb-1">What's Deliberately Excluded</h3>
                {renderValue(sections.deliberately_excluded)}
              </div>
            </CardContent>
          </Card>

          {/* Founder Fit Section */}
          <Card className="mb-6">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <User className="w-5 h-5 text-blue-600" />
                Founder Fit
              </CardTitle>
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-4">
              <div className="field">
                <h3 className="text-sm font-medium text-muted-foreground mb-1">Founder Advantage</h3>
                {renderValue(sections.founder_advantage)}
              </div>
              <div className="field">
                <h3 className="text-sm font-medium text-muted-foreground mb-1">Why Now?</h3>
                {renderValue(sections.why_now)}
              </div>
            </CardContent>
          </Card>

          {/* Offer & Pricing Section */}
          <Card className="mb-6">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <DollarSign className="w-5 h-5 text-green-600" />
                Offer & Pricing
              </CardTitle>
            </CardHeader>
            <CardContent className="grid md:grid-cols-3 gap-4">
              <div className="field">
                <h3 className="text-sm font-medium text-muted-foreground mb-1">First Offer</h3>
                {renderValue(sections.first_offer)}
              </div>
              <div className="field">
                <h3 className="text-sm font-medium text-muted-foreground mb-1">Simple Pricing</h3>
                {renderValue(sections.simple_pricing)}
              </div>
              <div className="field">
                <h3 className="text-sm font-medium text-muted-foreground mb-1">What Customer Gets</h3>
                {renderValue(sections.what_customer_gets)}
              </div>
            </CardContent>
          </Card>

          {/* Go-To-Market Section */}
          <Card className="mb-6">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Megaphone className="w-5 h-5 text-purple-600" />
                Go-To-Market
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="field">
                <h3 className="text-sm font-medium text-muted-foreground mb-1">Primary Acquisition Channel</h3>
                {renderValue(sections.primary_acquisition_channel)}
              </div>
              <div className="field">
                <h3 className="text-sm font-medium text-muted-foreground mb-1">First 10 Customers Plan</h3>
                {renderValue(sections.first_10_customers_plan)}
              </div>
              <div className="field">
                <h3 className="text-sm font-medium text-muted-foreground mb-1">Outreach Hook</h3>
                {renderValue(sections.outreach_hook)}
              </div>
            </CardContent>
          </Card>

          {/* Assumptions & Tests Section */}
          <Card className="mb-6">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <ClipboardCheck className="w-5 h-5 text-indigo-600" />
                Assumptions & Tests
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="field">
                <h3 className="text-sm font-medium text-muted-foreground mb-1">Top 3 Assumptions</h3>
                {renderValue(sections.top_3_assumptions)}
              </div>
              <div className="field">
                <h3 className="text-sm font-medium text-muted-foreground mb-1">7-Day Validation Tests</h3>
                {renderValue(sections.validation_tests)}
              </div>
              <div className="field">
                <h3 className="text-sm font-medium text-muted-foreground mb-1">Success Criteria</h3>
                {renderValue(sections.success_criteria)}
              </div>
            </CardContent>
          </Card>

          {/* 30-Day Action Plan Section */}
          <Card className="mb-6">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <CalendarDays className="w-5 h-5 text-pink-600" />
                30-Day Action Plan
              </CardTitle>
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-4">
              <div className="field">
                <h3 className="text-sm font-medium text-muted-foreground mb-1">Week 1</h3>
                {renderValue(sections.week_1)}
              </div>
              <div className="field">
                <h3 className="text-sm font-medium text-muted-foreground mb-1">Week 2</h3>
                {renderValue(sections.week_2)}
              </div>
              <div className="field">
                <h3 className="text-sm font-medium text-muted-foreground mb-1">Week 3</h3>
                {renderValue(sections.week_3)}
              </div>
              <div className="field">
                <h3 className="text-sm font-medium text-muted-foreground mb-1">Week 4</h3>
                {renderValue(sections.week_4)}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bottom CTAs */}
        <div className="grid md:grid-cols-2 gap-4 mt-8 no-print">
          <Link to="/canvas">
            <Card className="h-full hover:border-primary/50 transition-colors cursor-pointer" data-testid="create-new-canvas-card">
              <CardContent className="p-6 flex flex-col items-center text-center">
                <Share2 className="w-8 h-8 text-primary mb-3" />
                <h3 className="font-semibold mb-2">Create Another Canvas</h3>
                <p className="text-sm text-muted-foreground">Test a different business idea</p>
              </CardContent>
            </Card>
          </Link>
          <a href="https://calendly.com" target="_blank" rel="noopener noreferrer" onClick={trackBookingClick}>
            <Card className="h-full hover:border-secondary/50 transition-colors cursor-pointer" data-testid="book-strategy-call-card">
              <CardContent className="p-6 flex flex-col items-center text-center">
                <Calendar className="w-8 h-8 text-secondary mb-3" />
                <h3 className="font-semibold mb-2">Book a Strategy Call</h3>
                <p className="text-sm text-muted-foreground">Get expert feedback on your canvas</p>
              </CardContent>
            </Card>
          </a>
        </div>
      </div>
    </div>
  );
}
